import axios from 'axios';
import { api } from '../../lib/api.ts';

// ── Types (mirror the backend /api/site bundle) ──────────────────────────────

export type FieldKind = 'text' | 'richtext' | 'docref';
export type FeedSource = 'wapmeet' | 'wappro' | 'office';
export type MenuGroup =
  | 'about' | 'services' | 'decisions' | 'finance'
  | 'complaints' | 'rti' | 'participation';

export interface FieldValue { dv: string; en: string }

export interface FieldDef {
  key: string;
  kind: FieldKind;
  labelDv: string;
  labelEn: string;
  source?: FeedSource;
}

export interface GroupBundle {
  group: MenuGroup;
  titleDv: string;
  titleEn: string;
  mandated: boolean;
  fields: FieldDef[];
  content: Record<string, FieldValue>;
}

export interface OptionalBundle {
  id: string;
  titleDv: string;
  titleEn: string;
  enabled: boolean;
}

export interface SiteDocument {
  id: string;
  group: string | null;
  field: string | null;
  titleDv: string;
  titleEn: string;
  path: string;
  sizeBytes: number;
  createdAt?: string;
}

// The theme JSON the public renderer (Lagoon template) consumes.
export interface SiteTheme {
  primary?: string;    // brand colour (reef line / links / accents)
  ink?: string;        // authority text colour
  logo?: string;       // masthead logo image URL
  hero?: string;       // home hero image URL
  nameDv?: string;     // council name (Thaana)
  nameEn?: string;     // council name (Latin)
  taglineDv?: string;
  taglineEn?: string;
  typePresetDv?: string;  // Dhivehi/Thaana typography preset id
  typePresetEn?: string;  // English/Latin typography preset id
  menuOrder?: string[];   // ordered menu section ids (builder)
  menuLabels?: Record<string, { dv?: string; en?: string }>; // per-section menu label overrides
  notifyEmail?: string;   // where service-form submission notifications are sent
}

export type SiteStatus = 'DRAFT' | 'PUBLISHED';

export interface SiteInfo {
  id: string;
  templateId: string;
  theme: SiteTheme;
  subdomain: string;
  customDomain: string | null;
  status: string; // 'DRAFT' | 'PUBLISHED'
}

export interface ComplianceGap {
  group: MenuGroup;
  field: string;
  labelDv: string;
  labelEn: string;
}

export type FieldType = 'text' | 'textarea' | 'email' | 'phone' | 'number' | 'date' | 'select' | 'checkbox' | 'file';
export interface FormOption { dv: string; en: string }
export interface FormField {
  id: string;
  type: FieldType;
  labelDv: string;
  labelEn: string;
  required: boolean;
  options?: FormOption[];
}

export interface Service {
  id: string;
  siteId: string;
  position: number;
  slug: string;
  published: boolean;
  nameDv: string; nameEn: string;
  summaryDv: string; summaryEn: string;
  feeDv: string; feeEn: string;
  sectionDv: string; sectionEn: string;
  howToApplyDv: string; howToApplyEn: string;
  requiredDocsDv: string; requiredDocsEn: string;
  formEnabled: boolean;
  formFields: FormField[];
  formNoteDv: string; formNoteEn: string;
  formSuccessDv: string; formSuccessEn: string;
}

// Editable fields on a service (no identity/order/slug — those are server-managed).
export type ServiceInput = Partial<
  Pick<
    Service,
    | 'nameDv' | 'nameEn' | 'summaryDv' | 'summaryEn' | 'feeDv' | 'feeEn'
    | 'sectionDv' | 'sectionEn' | 'howToApplyDv' | 'howToApplyEn' | 'requiredDocsDv' | 'requiredDocsEn' | 'published'
    | 'formEnabled' | 'formFields' | 'formNoteDv' | 'formNoteEn' | 'formSuccessDv' | 'formSuccessEn'
  >
>;

export interface SubmissionFile { name: string; size: number; mime: string }
export interface ServiceSubmission {
  id: string;
  serviceId: string;
  data: Record<string, string>;
  files: Record<string, SubmissionFile>;
  status: 'new' | 'read';
  createdAt: string;
}

export interface SiteBundle {
  site: SiteInfo;
  groups: GroupBundle[];
  optional: OptionalBundle[];
  documents: SiteDocument[];
  services: Service[];
  submissionCounts: Record<string, number>;
  complianceGaps: ComplianceGap[];
}

// ── Error helper ─────────────────────────────────────────────────────────────

// Server error responses are `{ error: '<code>' }`. Pull the code out (or null).
export function apiErrorCode(e: unknown): string | null {
  if (axios.isAxiosError(e)) {
    const body = e.response?.data as { error?: unknown } | undefined;
    if (body && typeof body.error === 'string') return body.error;
  }
  return null;
}

export function siteNotProvisioned(e: unknown): boolean {
  return axios.isAxiosError(e) && e.response?.status === 404 && apiErrorCode(e) === 'site_not_found';
}

// ── Client ───────────────────────────────────────────────────────────────────

export const siteApi = {
  get: () => api.get<SiteBundle>('/site').then((r) => r.data),

  putSection: (group: string, fields: Record<string, FieldValue>) =>
    api.put<{ group: GroupBundle }>(`/site/section/${group}`, { fields }).then((r) => r.data.group),

  patchTheme: (patch: { templateId?: string; theme?: SiteTheme; status?: SiteStatus; customDomain?: string | null }) =>
    api.patch<{ site: SiteInfo }>('/site/theme', patch).then((r) => r.data.site),

  setOptional: (sectionId: string, enabled: boolean) =>
    api.put<{ optional: OptionalBundle }>(`/site/optional/${sectionId}`, { enabled }).then((r) => r.data.optional),

  uploadDocument: (input: { file: File; titleDv: string; titleEn: string; group?: string; field?: string }) => {
    const fd = new FormData();
    fd.append('file', input.file);
    fd.append('titleDv', input.titleDv);
    fd.append('titleEn', input.titleEn);
    if (input.group) fd.append('group', input.group);
    if (input.field) fd.append('field', input.field);
    return api.post<{ document: SiteDocument }>('/site/documents', fd).then((r) => r.data.document);
  },

  deleteDocument: (id: string) => api.delete(`/site/documents/${id}`).then(() => undefined),

  createService: (input: ServiceInput) => api.post<{ service: Service }>('/site/services', input).then((r) => r.data.service),
  updateService: (id: string, input: ServiceInput) => api.patch<{ service: Service }>(`/site/services/${id}`, input).then((r) => r.data.service),
  deleteService: (id: string) => api.delete(`/site/services/${id}`).then(() => undefined),
  reorderServices: (ids: string[]) => api.post<{ services: Service[] }>('/site/services-reorder', { ids }).then((r) => r.data.services),

  listSubmissions: (serviceId: string) => api.get<{ submissions: ServiceSubmission[] }>(`/site/services/${serviceId}/submissions`).then((r) => r.data.submissions),
  setSubmissionStatus: (id: string, status: 'new' | 'read') => api.patch<{ submission: ServiceSubmission }>(`/site/submissions/${id}`, { status }).then((r) => r.data.submission),
  deleteSubmission: (id: string) => api.delete(`/site/submissions/${id}`).then(() => undefined),

  // Short-lived token for the live-preview iframe.
  previewToken: () => api.get<{ token: string }>('/site/preview-token').then((r) => r.data.token),
};

// Same-origin URL the preview iframe points at. `r` busts the frame on refresh.
export function previewUrl(token: string, lang: 'dv' | 'en', path: string, r: number): string {
  const p = path ? `/${path}` : '/';
  const qs = new URLSearchParams({ token, lang, path: p, r: String(r) });
  return `/api/site-preview/render?${qs.toString()}`;
}
